import { useEffect, useMemo, useState } from "react";
import "./RegisterFormPublisher.css";

export default function RegisterFormPublisher({
  id = "register",
  eyebrow = "REGISTER",
  title = "Already Registered?",
  loginLabel = "Log in",
  submitLabel = "Submit",
  fields = {},
  onSubmit,
  onLogin,
  className = "",
  style = {},
}) {
  const communicationOptions = useMemo(() => fields.communicationOptions || [], [fields.communicationOptions]);
  const defaultComm = useMemo(() => communicationOptions[0]?.value || "", [communicationOptions]);
  const revenueOptions = useMemo(() => fields.revenueOptions || [
    { label: "0 - 20K", value: "0-20k" },
    { label: "20K - 50K", value: "20-50k" },
    { label: "50K - 100K", value: "50-100k" },
    { label: "100K - 500K", value: "100-500k" },
    { label: "500K+", value: "500k+" },
  ], [fields.revenueOptions]);

  const [values, setValues] = useState({
    company: "",
    contactPerson: "",
    email: "",
    country: "",
    communicationPlatform: defaultComm,
    communicationHandle: "",
    appLink: "",
    dau: "",
    revenue: revenueOptions[0]?.value || "",
    message: "",
  });
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    setValues(prev => ({
      ...prev,
      communicationPlatform: defaultComm,
      revenue: revenueOptions[0]?.value || "",
    }));
  }, [defaultComm, revenueOptions]);

  useEffect(() => {
    let cancelled = false;
    async function loadCountries() {
      try {
        const res = await fetch("https://restcountries.com/v3.1/all?fields=name");
        if (!res.ok) throw new Error("Failed to fetch countries");
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          const list = data
            .map(item => item?.name?.common)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCountries(list);
        }
      } catch {
        if (!cancelled) {
          setCountries(["United States", "United Kingdom", "Germany", "France", "Bulgaria"]);
        }
      }
    }
    loadCountries();
    return () => { cancelled = true; };
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit?.(values);
  }

  const getField = (key, fallbackLabel, fallbackPlaceholder) => ({
    label: fields[key]?.label || fallbackLabel,
    placeholder: fields[key]?.placeholder || fallbackPlaceholder,
  });

  const company = getField("company", "Company", "Company name");
  const contactPerson = getField("contactPerson", "Contact person", "Contact");
  const email = getField("email", "Email", "E-mail");
  const country = getField("country", "Country", "Select country");
  const communicationLabel = fields.communicationLabel || "Communication Platform";
  const communicationHandle = getField("communicationHandle", "Communication account", "Username");
  const appLink = getField("appLink", "App/Website link", "Link");
  const dau = getField("dau", "Daily Active Users", "Active users");
  const revenue = getField("revenue", "Monthly Revenue", "Revenue");
  const messageField = {
    label: fields.message?.label || "Message",
    helper: fields.message?.helper || "",
    placeholder: fields.message?.placeholder || "Type here",
  };

  return (
    <section id={id} className={`register-glass-section ${className}`.trim()} style={style}>
      <div className="register-glass-wrap">


        <div className="register-card">
          <header className="register-head">
            <p className="register-eyebrow">{eyebrow}</p>
            <h2 className="register-title">{title}</h2>
            <button type="button" className="register-login-btn" onClick={onLogin}>
              <span>{loginLabel.toUpperCase()}</span>
            </button>
          </header>
          <span className="register-dot register-dot--tl" aria-hidden="true" />
          <span className="register-dot register-dot--tr" aria-hidden="true" />
          <span className="register-dot register-dot--bl" aria-hidden="true" />
          <span className="register-dot register-dot--br" aria-hidden="true" />
          <span className="register-dot register-dot--mid" aria-hidden="true" />

          <form className="register-form" onSubmit={handleSubmit}>
            <div className="register-row register-row--two">
              <label className="register-field">
                <span className="register-label">{company.label}</span>
                <input
                  type="text"
                  name="company"
                  placeholder={company.placeholder}
                  value={values.company}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="register-field">
                <span className="register-label">{contactPerson.label}</span>
                <input
                  type="text"
                  name="contactPerson"
                  placeholder={contactPerson.placeholder}
                  value={values.contactPerson}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <div className="register-row register-row--two">
              <label className="register-field">
                <span className="register-label">{email.label}</span>
                <input
                  type="email"
                  name="email"
                  placeholder={email.placeholder}
                  value={values.email}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="register-field">
                <span className="register-label">{country.label}</span>
                <div className="register-select-wrap">
                  <select
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>{country.placeholder}</option>
                    {countries.map(countryName => (
                      <option key={countryName} value={countryName}>{countryName}</option>
                    ))}
                  </select>
                </div>
              </label>
            </div>

            <div className="register-platform">
              <span className="register-label">{communicationLabel}</span>
              <div className="register-platform-options">
                {communicationOptions.map(opt => (
                  <label key={opt.value} className="register-radio">
                    <input
                      type="radio"
                      name="communicationPlatform"
                      value={opt.value}
                      checked={values.communicationPlatform === opt.value}
                      onChange={handleChange}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="register-row register-row--two">
              <label className="register-field">
                <span className="register-label">{communicationHandle.label}</span>
                <input
                  type="text"
                  name="communicationHandle"
                  placeholder={communicationHandle.placeholder}
                  value={values.communicationHandle}
                  onChange={handleChange}
                  required
                />
              </label>

              <label className="register-field">
                <span className="register-label">{appLink.label}</span>
                <input
                  type="text"
                  name="appLink"
                  placeholder={appLink.placeholder}
                  value={values.appLink}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <div className="register-row register-row--two">
              <label className="register-field">
                <span className="register-label">{dau.label}</span>
                <input
                  type="text"
                  name="dau"
                  placeholder={dau.placeholder}
                  value={values.dau}
                  onChange={handleChange}
                />
              </label>

              <label className="register-field">
                <span className="register-label">{revenue.label}</span>
                <div className="register-select-wrap">
                  <select
                    name="revenue"
                    value={values.revenue}
                    onChange={handleChange}
                  >
                    {revenueOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            </div>

            <label className="register-field">
              <span className="register-label">
                {messageField.label}
                {messageField.helper && <small>{messageField.helper}</small>}
              </span>
              <textarea
                name="message"
                placeholder={messageField.placeholder}
                rows={6}
                value={values.message}
                onChange={handleChange}
              />
            </label>

            <div className="register-actions">
              <button type="submit" className="register-submit">
                <span>{submitLabel.toUpperCase()}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
